import React from 'react';
import { Link } from 'react-router-dom';
import {
  Zap, Search, Brain, Mail, Phone, BarChart3, ArrowRight, Check, Star,
  Users, Target, Sparkles, Flame, Megaphone, Rocket, ShieldCheck,
  TrendingUp, MessageSquare, Calendar,
} from 'lucide-react';

const Landing = () => {
  return (
    <div className="min-h-screen bg-[#F9FAFB] overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center shadow-glow">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-extrabold text-[17px]">Nexus<span className="gradient-text">Leads</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-slate-900">Features</a>
            <a href="#challenge" className="hover:text-slate-900">90-Day Challenge</a>
            <a href="#studio" className="hover:text-slate-900">Marketing Studio</a>
            <Link to="/pricing" className="hover:text-slate-900">Pricing</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-slate-900 hidden sm:inline px-3 py-2">Sign in</Link>
            <Link to="/register">
              <button className="btn-primary text-sm" data-testid="hero-register-btn">
                Get started <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-mesh" />
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-violet-500/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full bg-blue-500/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <div className="text-center max-w-4xl mx-auto">
            <span className="inline-flex items-center gap-1.5 badge-gradient mb-6">
              <Sparkles className="w-3 h-3" /> AI Client Acquisition System
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.02] tracking-tight text-slate-900">
              Land your <span className="gradient-text">first paying client</span>
              <br className="hidden sm:block" /> in 90 days. On autopilot.
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mt-7 max-w-2xl mx-auto leading-relaxed">
              The AI system that finds potential clients, writes the outreach,
              and gives you a daily plan — so consistent action becomes inevitable.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-9">
              <Link to="/register">
                <button className="btn-primary text-base px-7 py-3" data-testid="cta-start">
                  Start landing clients <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/pricing">
                <button className="btn-secondary text-base px-7 py-3">View pricing</button>
              </Link>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-xs text-slate-500 font-medium">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> No long-term contracts</span>
              <span className="inline-flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-amber-500" /> Real outcomes, not vanity metrics</span>
              <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-violet-600" /> AI-built, human-paced</span>
            </div>
          </div>

          {/* Hero preview cards */}
          <div className="relative max-w-5xl mx-auto mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
            <PreviewCard icon={Flame} label="Day 7 / 90" title="Today's Mission" lines={['Send 15 personalized DMs', 'Follow up with 5 warm leads', 'Post 1 case study']} accent="violet" />
            <PreviewCard icon={Users} label="Pipeline" title="32 potential clients" lines={['12 contacted', '4 replied', '2 calls booked']} accent="blue" />
            <PreviewCard icon={Megaphone} label="Marketing Studio" title="Ad Creative ready" lines={['1080×1080 IG · Honest assessment 8/10', 'Playbook tailored to your offer', 'Website audit done']} accent="emerald" />
          </div>
        </div>
      </section>

      {/* THREE-PILLAR ANCHOR */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="text-center mb-14">
          <p className="text-[11px] font-bold tracking-[0.18em] text-violet-700 uppercase mb-3">How it works</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight">
            Find. Engage. <span className="gradient-text">Close.</span>
          </h2>
          <p className="text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
            One system that handles the full client-acquisition motion.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Pillar
            icon={Search}
            stage="01"
            title="Find potential clients"
            body="Search by niche + state. Get warm-lead scoring, contact info, and AI-tailored insights for every prospect."
            bullets={['10+ optimized niches', 'All 50 US states', 'Warm-lead indicator']}
            accent="violet"
          />
          <Pillar
            icon={MessageSquare}
            stage="02"
            title="Engage daily"
            body="AI writes emails and call scripts in your voice. Daily 90-Day Challenge keeps you in motion when momentum is hard."
            bullets={['AI Email Writer', 'AI Call Scripts', '90-Day Challenge']}
            accent="blue"
            featured
          />
          <Pillar
            icon={Target}
            stage="03"
            title="Close & deliver"
            body="Track every reply, follow-up, and deal. Marketing Studio gives you ad creatives + playbooks for warm-up campaigns."
            bullets={['Built-in CRM', 'Marketing Studio', 'Performance analytics']}
            accent="emerald"
          />
        </div>
      </section>

      {/* 90-DAY CHALLENGE SHOWCASE */}
      <section id="challenge" className="bg-white border-y border-slate-200 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 badge-gradient mb-5">
              <Flame className="w-3 h-3" /> Flagship Program
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight">
              The <span className="gradient-text">90-Day Client Acquisition Challenge</span>.
            </h2>
            <p className="text-lg text-slate-600 mt-5">
              Tell us your service and price. We build a personalized day-by-day plan with measurable
              outreach quotas, follow-ups, content, and sales actions. You ship daily — we hold you accountable.
            </p>
            <ul className="space-y-3 mt-7">
              {[
                ['Daily outreach quota', 'Personalized to your weekly hours.'],
                ['AI accountability coach', "Graded check-ins. Knows when you're slacking."],
                ['Adaptive recalibration', 'Falling behind? AI shifts tasks or rebuilds the plan.'],
                ['Streak system', 'Don\'t break the chain.'],
              ].map(([t, s]) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </span>
                  <div>
                    <p className="font-semibold text-slate-900">{t}</p>
                    <p className="text-sm text-slate-500">{s}</p>
                  </div>
                </li>
              ))}
            </ul>
            <Link to="/register" className="inline-block mt-8">
              <button className="btn-primary" data-testid="challenge-cta">
                <Rocket className="w-4 h-4" /> Start the challenge
              </button>
            </Link>
          </div>

          {/* Visual preview */}
          <div className="card-base p-6 sm:p-8 shadow-elevated">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <span className="icon-halo"><Flame className="w-4 h-4 text-violet-700" strokeWidth={2.5} /></span>
                <div>
                  <p className="text-sm font-bold text-slate-900">Day 36 of 90</p>
                  <p className="text-xs text-slate-500">Outreach Engine week</p>
                </div>
              </div>
              <span className="badge-gradient">7-day 🔥</span>
            </div>
            <div className="progress-track mb-2"><div className="progress-fill" style={{width: '40%'}} /></div>
            <p className="text-xs text-slate-500 mb-5">40% complete</p>
            <div className="space-y-2">
              {[
                { c: true,  l: 'Send 15 personalized DMs to ICPs', t: 'Outreach' },
                { c: true,  l: 'Follow up with 5 prospects from D9', t: 'Follow-up' },
                { c: false, l: 'Post 1 short case study on LinkedIn', t: 'Content' },
                { c: false, l: 'Book 2 discovery calls for next week', t: 'Sales' },
              ].map((x, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${x.c ? 'bg-emerald-50/60 border-emerald-100' : 'bg-slate-50/60 border-slate-200'}`}>
                  <span className={`w-5 h-5 rounded-md flex items-center justify-center ${x.c ? 'bg-emerald-500' : 'border-2 border-slate-300 bg-white'}`}>
                    {x.c && <Check className="w-3 h-3 text-white" strokeWidth={3.5} />}
                  </span>
                  <p className={`text-sm flex-1 ${x.c ? 'line-through text-slate-500' : 'text-slate-900 font-medium'}`}>{x.l}</p>
                  <span className="badge-info text-[10px]">{x.t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MARKETING STUDIO */}
      <section id="studio" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1 grid grid-cols-2 gap-3">
            <div className="card-base p-5 col-span-2">
              <div className="flex items-center gap-2 mb-2">
                <span className="badge-gradient text-[10px]">Honest AI Assessment</span>
              </div>
              <p className="text-sm text-slate-700">"Your offer is fundable but pricing is 30% under-priced for the niche. Recommend testing $1,200 entry tier."</p>
              <div className="progress-track mt-3"><div className="progress-fill" style={{width: '78%'}} /></div>
              <p className="text-xs text-slate-500 mt-1">Feasibility 7.8/10</p>
            </div>
            <div className="card-base p-5">
              <Megaphone className="w-5 h-5 text-violet-700 mb-2" />
              <p className="text-sm font-semibold text-slate-900">Ad Creative</p>
              <p className="text-xs text-slate-500">1080×1080 · IG ready</p>
            </div>
            <div className="card-base p-5">
              <Calendar className="w-5 h-5 text-violet-700 mb-2" />
              <p className="text-sm font-semibold text-slate-900">Marketing Playbook</p>
              <p className="text-xs text-slate-500">Channel-specific 90-day map</p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="inline-flex items-center gap-1.5 badge-gradient mb-5">
              <Megaphone className="w-3 h-3" /> Marketing Studio
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight">
              Ad creatives. Playbooks. <span className="gradient-text">Real assessments.</span>
            </h2>
            <p className="text-lg text-slate-600 mt-5">
              Generate scroll-stopping ad images, channel-specific marketing playbooks,
              and live website audits — with brutally honest feasibility scores instead of generic hype.
            </p>
            <ul className="space-y-2.5 mt-6">
              {['AI ad creatives (Nano Banana)', 'Multi-channel marketing playbooks', 'Website audit + feasibility score', 'Recommended channel for your offer'].map(x => (
                <li key={x} className="flex items-center gap-2 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" /> {x}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF / STATS */}
      <section className="bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 text-white py-20 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 hero-mesh opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold leading-tight">
            Built for people who are <span className="gradient-text">tired of theory</span>.
          </h2>
          <p className="text-lg text-violet-100 mt-4 max-w-2xl mx-auto">
            NexusLeads is for solopreneurs, freelancers, and agencies who need to ship outreach today
            — not next quarter.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
            {[
              ['10+', 'Optimized niches'],
              ['50', 'US states'],
              ['90', 'Days to first client'],
              ['100%', 'Outcome-focused'],
            ].map(([v, l]) => (
              <div key={l}>
                <p className="font-heading text-4xl sm:text-5xl font-extrabold gradient-text">{v}</p>
                <p className="text-sm text-violet-200 mt-2">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="card-base p-8 sm:p-12 lg:p-16 relative overflow-hidden text-center shadow-elevated">
          <div className="absolute inset-0 hero-mesh opacity-90" />
          <div className="relative">
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight">
              Stop hoping. <span className="gradient-text">Start landing clients.</span>
            </h2>
            <p className="text-lg text-slate-700 mt-4 max-w-xl mx-auto">
              Sign up in 90 seconds. Pick your niche. Run the 90-Day Challenge.
              Get to your first paying client.
            </p>
            <Link to="/register" className="inline-block mt-8">
              <button className="btn-primary text-base px-7 py-3" data-testid="cta-bottom">
                Get started <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center">
              <Zap className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-heading font-bold text-slate-900">NexusLeads</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-900">Terms</Link>
            <Link to="/refund" className="hover:text-slate-900">Refunds</Link>
          </div>
          <p>© {new Date().getFullYear()} NexusLeads. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

// =================== Subcomponents ===================

const PreviewCard = ({ icon: Icon, label, title, lines, accent }) => {
  const accents = {
    violet: 'from-violet-500 to-violet-600',
    blue: 'from-blue-500 to-blue-600',
    emerald: 'from-emerald-500 to-emerald-600',
  };
  return (
    <div className="card-base shadow-elevated p-5 backdrop-blur-sm bg-white/95">
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-9 h-9 rounded-xl bg-gradient-to-br ${accents[accent]} flex items-center justify-center text-white shadow-sm`}>
          <Icon className="w-4 h-4" strokeWidth={2.5} />
        </span>
        <p className="text-[10px] font-bold tracking-[0.16em] text-slate-500 uppercase">{label}</p>
      </div>
      <p className="font-heading font-bold text-slate-900 text-lg">{title}</p>
      <ul className="mt-3 space-y-1.5">
        {lines.map(l => (
          <li key={l} className="flex items-center gap-2 text-xs text-slate-600">
            <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" /> {l}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Pillar = ({ icon: Icon, stage, title, body, bullets, accent, featured }) => {
  const accents = {
    violet:  'from-violet-500 to-violet-600',
    blue:    'from-blue-500 to-blue-600',
    emerald: 'from-emerald-500 to-emerald-600',
  };
  return (
    <div className={`relative ${featured ? 'card-gradient-border' : ''}`}>
      <div className={`${featured ? 'p-7' : 'card-base card-hover p-7'}`}>
        <div className="flex items-center justify-between mb-5">
          <span className={`w-12 h-12 rounded-xl bg-gradient-to-br ${accents[accent]} flex items-center justify-center text-white shadow-sm`}>
            <Icon className="w-5 h-5" strokeWidth={2.5} />
          </span>
          <span className="font-heading font-extrabold text-3xl text-slate-200">{stage}</span>
        </div>
        <h3 className="font-heading font-extrabold text-2xl text-slate-900 leading-tight">{title}</h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
        <ul className="space-y-1.5 mt-5">
          {bullets.map(b => (
            <li key={b} className="flex items-center gap-2 text-sm text-slate-700">
              <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" /> {b}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Landing;
